import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ShieldCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ThemeToggle from "./ThemeToggle";

const LINKS = [
  { to: "/", label: "Accueil" },
  { to: "/depannage", label: "Dépannage" },
  { to: "/application", label: "Application" },
  { to: "/espaces", label: "Espaces" },
  { to: "/solutions", label: "Solutions" },
  { to: "/metiers", label: "Métiers" },
  { to: "/annonces", label: "Annonces" },
  { to: "/tarifs", label: "Tarifs" },
  { to: "/conseils", label: "Conseils" },
  { to: "/rejoindre", label: "Rejoindre" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-navy/80 backdrop-blur-2xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
        <Link to="/" data-testid="logo-link" className="group flex items-center gap-2.5">
          <img src="/logo-s.png" alt="Logo Speed Détresse" className="h-9 w-9 rounded-full object-cover transition-transform group-hover:scale-105" />
          <span className="font-serif text-xl tracking-tight text-white">
            Speed <span className="text-gold">Détresse</span>
          </span>
        </Link>

        <div className="hidden items-center gap-4 xl:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                `relative text-[13px] font-medium tracking-wide transition-colors ${
                  isActive ? "text-gold" : "text-slate-300 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden items-center gap-3 xl:flex">
          <ThemeToggle testid="theme-toggle-desktop" />
          <Link
            to="/admin"
            data-testid="nav-espace-client"
            className="rounded-full border border-gold/50 px-4 py-2 text-sm font-medium text-gold transition-colors hover:bg-gold/10"
          >
            Administration
          </Link>
          <Link
            to="/tarifs"
            data-testid="nav-souscrire"
            className="rounded-full bg-gold px-5 py-2 text-sm font-semibold text-navy transition-colors hover:bg-gold-light"
          >
            Préparer l'abonnement
          </Link>
        </div>

        <div className="flex items-center gap-2 xl:hidden">
          <ThemeToggle testid="theme-toggle-mobile" />
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="text-slate-200"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-t border-white/10 bg-navy/95 backdrop-blur-2xl xl:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-3 text-base ${isActive ? "text-gold" : "text-slate-200"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="mt-2 flex gap-3">
                <Link to="/admin" className="flex-1 rounded-full border border-gold/50 px-4 py-2.5 text-center text-sm text-gold">
                  Administration
                </Link>
                <Link to="/tarifs" className="flex-1 rounded-full bg-gold px-4 py-2.5 text-center text-sm font-semibold text-navy">
                  Préparer l'abonnement
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
